import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Code,
  Copy,
  Check,
  FileCode,
  Image,
  Zap
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrackingPage = () => {
  const { getAuthHeader } = useAuth();
  const [trackingCode, setTrackingCode] = useState(null);
  const [copiedScript, setCopiedScript] = useState(false);
  const [copiedPixel, setCopiedPixel] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrackingCode = async () => {
      try {
        const response = await axios.get(`${API}/tracking-code`, {
          headers: getAuthHeader()
        });
        setTrackingCode(response.data);
      } catch (error) {
        toast.error("Failed to fetch tracking code");
      } finally {
        setLoading(false);
      }
    };
    fetchTrackingCode();
  }, []);

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "script") {
        setCopiedScript(true);
        setTimeout(() => setCopiedScript(false), 2000);
      } else {
        setCopiedPixel(true);
        setTimeout(() => setCopiedPixel(false), 2000);
      }
      toast.success("Copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="tracking-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight flex items-center gap-3">
          <Code className="w-7 h-7 text-primary" />
          Tracking Pixel
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Embed these snippets on any website to track visitor IPs
        </p>
      </div>

      {/* Info Card */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="text-sm font-medium">How it works</p>
              <p className="text-sm text-muted-foreground mt-1">
                Add either the JavaScript snippet or the image pixel to your website's HTML. 
                When visitors load your page, their IP address, location, device info, and browser 
                details will be automatically logged to your dashboard.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tracking Code Tabs */}
      <Tabs defaultValue="script" className="space-y-4">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="script" className="gap-2" data-testid="script-tab">
            <FileCode className="w-4 h-4" />
            JavaScript (Recommended)
          </TabsTrigger>
          <TabsTrigger value="pixel" className="gap-2" data-testid="pixel-tab">
            <Image className="w-4 h-4" />
            Image Pixel
          </TabsTrigger>
        </TabsList>

        {/* JavaScript Tab */}
        <TabsContent value="script">
          <Card className="bg-card/50 border-border" data-testid="script-card">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FileCode className="w-4 h-4 text-primary" />
                JavaScript Tracking Script
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Add this script tag to the <code className="bg-secondary px-1.5 py-0.5 rounded text-xs font-mono">&lt;head&gt;</code> or 
                before the closing <code className="bg-secondary px-1.5 py-0.5 rounded text-xs font-mono">&lt;/body&gt;</code> tag of your HTML.
              </p>
              
              <div className="relative">
                <pre className="bg-background p-4 rounded-md border border-border overflow-x-auto">
                  <code className="text-sm font-mono text-primary">
                    {trackingCode?.script_tag}
                  </code>
                </pre>
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute top-2 right-2 gap-2"
                  onClick={() => copyToClipboard(trackingCode?.script_tag, "script")}
                  data-testid="copy-script-btn"
                >
                  {copiedScript ? (
                    <>
                      <Check className="w-4 h-4 text-emerald-500" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </Button>
              </div>

              <div className="bg-secondary/50 p-4 rounded-md space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Advantages</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Captures full page path and referrer information</li>
                  <li>• Async loading - doesn't block page render</li>
                  <li>• Works with JavaScript-heavy SPAs</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Image Pixel Tab */}
        <TabsContent value="pixel">
          <Card className="bg-card/50 border-border" data-testid="pixel-card">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Image className="w-4 h-4 text-primary" />
                1x1 Transparent Image Pixel
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Add this invisible image anywhere in your HTML. Works even when JavaScript is disabled.
              </p>
              
              <div className="relative">
                <pre className="bg-background p-4 rounded-md border border-border overflow-x-auto">
                  <code className="text-sm font-mono text-primary">
                    {trackingCode?.pixel_tag}
                  </code>
                </pre>
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute top-2 right-2 gap-2"
                  onClick={() => copyToClipboard(trackingCode?.pixel_tag, "pixel")}
                  data-testid="copy-pixel-btn"
                >
                  {copiedPixel ? (
                    <>
                      <Check className="w-4 h-4 text-emerald-500" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </Button>
              </div>

              <div className="bg-secondary/50 p-4 rounded-md space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Advantages</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Works without JavaScript</li>
                  <li>• Compatible with email tracking</li>
                  <li>• Minimal footprint (43 bytes)</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Usage Examples */}
      <Card className="bg-card/50 border-border">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Example Integration</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="bg-background p-4 rounded-md border border-border overflow-x-auto text-xs">
            <code className="font-mono text-muted-foreground">{`<!DOCTYPE html>
<html>
<head>
    <title>My Website</title>
    ${trackingCode?.script_tag || '<!-- tracking script -->'}
</head>
<body>
    <h1>Welcome to my site</h1>
    
    <!-- Or use the image pixel instead -->
    ${trackingCode?.pixel_tag || '<!-- tracking pixel -->'}
</body>
</html>`}</code>
          </pre>
        </CardContent>
      </Card>
    </div>
  );
};

export default TrackingPage;
